import './SearchSelect.css';
import './../input.scss';
import React, { useState, ReactNode, useRef } from 'react';
import classnames from 'classnames';
import GlobalProps from '../../globalProps';
import { SelectableList } from '../../SelectableList/SelectableList';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { Tooltip } from '../../Tooltip/Tooltip';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { TextField } from '../TextField/TextField';
import { SimpleCheckbox } from '../Checkbox/Checkbox';
import * as PopperJS from 'popper.js';
import { TagsGroup } from '../../Tag/TagsGroup';
import { FormField } from '../../../model/form';
import { FormTagsInput } from '../TagsInput/FormTagsInput';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface Option {
  id: string;
  label?: string;
  content?: ReactNode;
  count?: number;
}

interface Props extends FormField<string[] | null>, GlobalProps {
  name?: string;
  label?: string;
  options: Option[];
  hiddenValues?: string[];
  error?: string;
  notFoundMessage?: ReactNode;
  description?: string;
  position?: PopperJS.Placement;
  disabled?: boolean;
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  customValue?: string;
  loading?: boolean;
  loadingMsg?: string;
  input?: boolean;
  maxWidth?: string | number;
}

function getLabelForId(id: string, options: Option[]): string | undefined {
  const item = options.find((val) => val.id === id);
  return item?.label || item?.id;
}

// Component
export function FormMultiSelect({
  value,
  label,
  options,
  hiddenValues,
  errors,
  notFoundMessage,
  description,
  disabled,
  icon,
  actions,
  required,
  className,

  touched,
  valid,
  loading,
  loadingMsg,
  position = 'bottom-start',
  onChange,
  input,
  maxWidth,
}: Props): JSX.Element {
  const parentRef = useRef<HTMLDivElement>(null);
  const displayErrors = touched && !valid;

  // Query hook for search
  const [query, setQuery] = useState<string>();
  // Active hook to display dropdown
  const [active, setActive] = useState(false);
  // Display all options or just the selected ones
  const [displayAllOptions, setDisplayAllOptions] = useState(true);
  // FormTagsInput current value
  const [hasInputValue, setHasInputValue] = useState(false);

  // Tag Handling
  function removeTagAtIndex(index: number): void {
    if (value) {
      const newValue = [...value];
      newValue.splice(index, 1);
      onChange?.(newValue);
    }
  }

  function addTag(idToAdd: string): void {
    if (value) {
      const newValue = [...value];
      if (newValue.includes(idToAdd)) {
        return removeTagAtIndex(newValue.findIndex((v) => v === idToAdd));
      } else {
        newValue.push(idToAdd);
      }
      onChange?.(newValue);
    } else {
      onChange?.([idToAdd]);
    }
    setQuery(undefined);
  }

  // Render
  function renderInput(): JSX.Element {
    if (input) {
      return (
        <div ref={parentRef}>
          <FormTagsInput
            value={value}
            onChange={onChange}
            onInputChange={(value) => {
              setHasInputValue(!!value);
            }}
            label={label}
          />
        </div>
      );
    }

    return (
      <div className="input__input-wrapper" ref={parentRef}>
        {label && (
          <label className="input__label">
            {label}
            {!required && ` (${t.general.optional})`}
          </label>
        )}
        <TagsGroup
          tags={value?.map((item, index) => ({
            id: index.toString(),
            label: getLabelForId(item, options),
          }))}
          onClear={() => onChange?.([])}
          onRemove={disabled ? undefined : removeTagAtIndex}
          disableTagRemove={disabled}
          actions={
            <div className="search-select__display-options">
              <Icon name="arrow-triangle-down--sm" />
            </div>
          }
        ></TagsGroup>
        {actions && <div className="input__actions">{actions}</div>}
        {displayErrors && <Icon name="alert-circle--sm" className="input__alert-circle" />}
      </div>
    );
  }

  function renderOptions(): React.ReactNode[] {
    let searchResults: string[] = [];

    if (query) {
      searchResults = [
        ...options.filter((option) => option.label?.toLowerCase().includes(query.trim().toLowerCase())),
      ].map((v) => v.id);

      if (searchResults.length === 0) {
        return [
          <div key="search-select__not-found-item" className="search-select__not-found-item">
            {notFoundMessage || t.errors.noResults}
          </div>,
        ];
      }
    }

    function displayOption(id: string, selected = false): boolean {
      if (query) {
        return displayAllOptions ? searchResults.includes(id) : selected && searchResults.includes(id);
      }
      return displayAllOptions ? true : selected;
    }

    return options
      .filter((v) => !hiddenValues?.find((id) => v.id === id))
      .map((v) => {
        const content = v.content || v.label || v.id;
        const isSelected = value?.includes(v.id);

        return (
          <div
            className={classnames('search-select__option', {
              'search-select__option--hidden': !displayOption(v.id, isSelected),
            })}
            key={v.id}
          >
            <SimpleCheckbox value={isSelected} readOnly label={content} />
          </div>
        );
      });
  }

  if (loading) {
    return <Spinner color="red" text={loadingMsg || t.actions.loading('')} />;
  }

  return (
    <div className={classnames('search-select', 'input', { 'input--with-icon': icon }, className)} style={{ maxWidth }}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': (value && value.length > 0) || active,
          'input__container--error': displayErrors,
        })}
      >
        {disabled ? (
          renderInput()
        ) : (
          <Tooltip
            style={{ width: parentRef.current?.clientWidth }}
            reference={renderInput()}
            className="search-select__select-menu"
            position={position}
            mode="click"
            gap="none"
            arrow={false}
            rounded={false}
            open={active}
            onChange={(open) => setActive(open)}
          >
            <>
              {options.length > 4 && (
                <div className="search-select__search">
                  <TextField icon="search--sm" value={query} onChange={(v) => setQuery(v as string)} />
                  <div
                    className={classnames('search-select__toggle-display', {
                      active: !displayAllOptions,
                    })}
                  >
                    <Tooltip
                      reference={
                        <Icon
                          name="list"
                          onClick={() => {
                            setDisplayAllOptions(!displayAllOptions);
                          }}
                        />
                      }
                      darkMode
                      position="top"
                    >
                      <small>{t.actions.displaySelectedOnly}</small>
                    </Tooltip>
                  </div>
                </div>
              )}
              <SelectableList
                className="search-select__select-menu-items"
                options={renderOptions()}
                resetHighlight={false}
                onSelect={(index) => {
                  if (!hasInputValue) {
                    addTag(options?.[index]?.id);
                  }
                }}
              />
            </>
          </Tooltip>
        )}
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={displayErrors}>
            {errors?.map((error) => {
              return (
                <div className="input__error-message" key={error}>
                  {t.errors[error] || error}
                </div>
              );
            })}
          </Collapse>
        </div>
      </div>
    </div>
  );
}
